import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import {
    Box,
    Typography,
    Paper,
    InputBase,
    Button,
    Select,
    MenuItem,
    Chip,
    Pagination,
    Menu,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Stack,
    FormControl,
    InputLabel,
    Checkbox,
    ListItemText,
    IconButton,
    CircularProgress,
    Alert,
    Grid,
    Tooltip,
} from "@mui/material";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from '@mui/icons-material/Search';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TuneIcon from '@mui/icons-material/Tune';

import useStyles from "../../assets/styles/SupMangComp.style";
import API from "../../utils/Axios.utils";
import useDebounce from "../../utils/useDebounce";
import { format } from "date-fns";
import { useRagSearch } from '../../hooks/useRagSearch';
import ParsedFiltersDisplay from './ParsedFiltersDisplay';

const AiCvSearchView = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { isSupplier, isStaff, isAdmin } = useAuth();
    const authData = localStorage.getItem("userLoggedIn");
    const token = authData ? JSON.parse(authData).token : null;

    const config = useMemo(() => ({
        headers: { Authorization: `Bearer ${token}` },
    }), [token]);

    // Pagination initialization from URL
    const initialPage = parseInt(searchParams.get("page")) || 1;
    const [listCount, setListCount] = useState(0);
    const [page, setPage] = useState(initialPage);
    const handlePageChange = (e, value) => {
        setPage(value);
    };

    // ---------- API data ----------
    const [profiles, setProfiles] = useState([]);

    // selection and filters
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedCandidatesData, setSelectedCandidatesData] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuRowId, setMenuRowId] = useState(null);
    const openMenu = Boolean(anchorEl);

    // Filter initialization from URL
    const initialSupplierId = searchParams.get("supplier_id") ? parseInt(searchParams.get("supplier_id")) : null;
    const initialCoreSkillIds = searchParams.get("core_skill_ids") ? searchParams.get("core_skill_ids").split(",").map(Number).filter(id => !isNaN(id)) : [];
    const initialDateFilter = searchParams.get("date_filter") || "all";
    const initialSearch = searchParams.get("search") || "";

    // Applied filters state
    const [appliedFilters, setAppliedFilters] = useState({
        supplier_id: initialSupplierId,
        core_skill_ids: initialCoreSkillIds,
        date_filter: initialDateFilter,
        search: initialSearch
    });

    const [statusFilter, setStatusFilter] = useState('All');

    // RAG Search State
    const [ragQuery, setRagQuery] = useState('');
    const [activeRagQuery, setActiveRagQuery] = useState('');

    const {
        data: ragData,
        isLoading: ragLoading,
        error: ragError
    } = useRagSearch(
        { query: activeRagQuery, page, pageSize: 10 },
        { enabled: activeRagQuery.trim().length >= 3 }
    );

    // Supplier and skills selection (for UI only)
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedCoreSkills, setSelectedCoreSkills] = useState([]);
    const [allCoreSkills, setAllCoreSkills] = useState([]);
    const [supplierData, setSupplierData] = useState([]);
    const [isInitialSyncDone, setIsInitialSyncDone] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [candidateSearchText, setCandidateSearchText] = useState(initialSearch);
    const [selectedDateFilter, setSelectedDateFilter] = useState(initialDateFilter);

    // Synchronize URL with state
    useEffect(() => {
        const params = new URLSearchParams();
        params.set("page", page.toString());
        if (appliedFilters.supplier_id) params.set("supplier_id", appliedFilters.supplier_id.toString());
        if (appliedFilters.core_skill_ids.length > 0) params.set("core_skill_ids", appliedFilters.core_skill_ids.join(","));
        if (appliedFilters.date_filter !== 'all') params.set("date_filter", appliedFilters.date_filter);
        if (appliedFilters.search) params.set("search", appliedFilters.search);
        setSearchParams(params, { replace: true });
    }, [page, appliedFilters, setSearchParams]);

    const fetchAllCoreSkills = useCallback(async () => {
        try {
            const res = await API.get("/api/core-skill/coreSkills", config);
            setAllCoreSkills(res.data.coreSkills || []);
        } catch (error) {
            console.error("Error fetching all core skills:", error);
        }
    }, [config]);

    useEffect(() => {
        fetchAllCoreSkills();
    }, [fetchAllCoreSkills]);

    useEffect(() => {
        if (!isInitialSyncDone && supplierData.length > 0 && allCoreSkills.length > 0) {
            if (appliedFilters.supplier_id) {
                const supplier = supplierData.find(s => s.supplier_id === appliedFilters.supplier_id);
                if (supplier) setSelectedSupplier(supplier);
            }
            if (appliedFilters.core_skill_ids.length > 0) {
                const skills = allCoreSkills.filter(skill => appliedFilters.core_skill_ids.includes(skill.id));
                setSelectedCoreSkills(skills);
            }
            setIsInitialSyncDone(true);
        }
    }, [supplierData, allCoreSkills, appliedFilters.supplier_id, appliedFilters.core_skill_ids, isInitialSyncDone]);

    const getSupplier = async () => {
        let url = `/api/users/getallsupplier`;
        if (searchText !== "") url += `?search=${debouncedQueryList}`;
        try {
            const res = await API.get(url, config);
            setSupplierData(res?.data?.supplier || []);
        } catch (error) {
            console.error("Error fetching suppliers:", error);
        }
    };
    const debouncedQueryList = useDebounce(searchText, 500);

    // The supplier filter is staff only (the API returns 403 to suppliers).
    useEffect(() => {
        if (isStaff) getSupplier();
    }, [debouncedQueryList, isStaff]);

    const handleSupplierChange = (event) => {
        const supplierId = event.target.value;
        if (supplierId === "") {
            setSelectedSupplier(null);
        } else {
            const supplier = supplierData.find(s => s.supplier_id === parseInt(supplierId));
            setSelectedSupplier(supplier);
        }
    };

    const handleCoreSkillChange = (event) => {
        const skillIds = event.target.value;
        const selectedSkills = allCoreSkills.filter(skill => skillIds.includes(skill.id));
        setSelectedCoreSkills(selectedSkills);
    };

    const getProfiles = useCallback(async () => {
        try {
            let url = `/api/profile/allprofile?page=${page}&pageSize=10`;
            if (appliedFilters.supplier_id) url += `&supplier_id=${appliedFilters.supplier_id}`;
            if (appliedFilters.core_skill_ids.length > 0) url += `&core_skill_ids=${appliedFilters.core_skill_ids.join(',')}`;
            if (appliedFilters.date_filter !== 'all') url += `&datefilter=${appliedFilters.date_filter}`;
            if (appliedFilters.search) url += `&search=${encodeURIComponent(appliedFilters.search.trim())}`;
            const res = await API.get(url, config);
            setProfiles(res?.data?.profile || []);
            setListCount(res?.data?.totalCount || 0);
        } catch (error) {
            console.error('Error fetching profiles:', error);
        }
    }, [page, appliedFilters, config]);

    useEffect(() => {
        getProfiles();
    }, [getProfiles]);

    const handleSubmitFilters = () => {
        setAppliedFilters({
            supplier_id: selectedSupplier?.supplier_id || null,
            core_skill_ids: selectedCoreSkills.map(skill => skill.id),
            date_filter: selectedDateFilter,
            search: candidateSearchText.trim()
        });
        setPage(1);
    };

    const handleClearFilters = () => {
        setSelectedSupplier(null);
        setSelectedCoreSkills([]);
        setSelectedDateFilter('all');
        setCandidateSearchText('');
        setStatusFilter('All');
        setAppliedFilters({
            supplier_id: null,
            core_skill_ids: [],
            date_filter: 'all',
            search: ''
        });
        setRagQuery('');
        setActiveRagQuery('');
        setPage(1);
    };

    const handleToggleOne = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
        setSelectedCandidatesData((prev) => {
            if (prev.some(c => c.profile_id === id)) {
                return prev.filter(c => c.profile_id !== id);
            } else {
                const row = isSemanticActive 
                    ? (ragData?.candidates || []).find(c => c.profile_id === id) 
                    : profiles.find(p => p.profile_id === id);
                return row ? [...prev, row] : prev;
            }
        });
    };

    const handleMenuOpen = (event, rowId) => {
        setAnchorEl(event.currentTarget);
        setMenuRowId(rowId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuRowId(null);
    };

    const extractFileNameFromUrl = (url) => {
        if (!url) return "";
        const parts = url.split("/");
        const lastPart = parts[parts.length - 1];
        return decodeURIComponent(lastPart.split("?")[0]);
    };

    const triggerDownload = (fileUrl, downloadUrl) => {
        const urlToUse = downloadUrl || fileUrl;
        if (!urlToUse) return;
        const fileName = extractFileNameFromUrl(urlToUse) || "file";
        const a = document.createElement('a');
        a.href = urlToUse;
        a.download = fileName;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { document.body.removeChild(a); }, 100);
    };

    const handleDownload = (id) => {
        const row = isSemanticActive ? (ragData?.candidates || []).find(c => c.profile_id === id) : profiles.find((p) => p.profile_id === id);
        if (!row || row.pipeline_type === 'non_resume_based' || row.Job?.pipeline_type === 'non_resume_based' || row.job_pipeline_type === 'non_resume_based') {
            handleMenuClose();
            return;
        }
        if (!row?.url && !row?.downloadUrl && !row?.resume_url) {
            handleMenuClose();
            return;
        }
        triggerDownload(row.resume_url || row.url, row.downloadUrl);
        handleMenuClose();
    };

    const handleBulkDownload = async () => {
        const ids = selectedIds;
        if (ids.length === 0) return;
        try {
            const response = await API.post(
                "/api/profile/allprofile/csv",
                { profile_ids: ids },
                { ...config, responseType: "blob" }
            );
            const blob = new Blob([response.data], { type: "application/zip" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "selected_resumes.zip");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading ZIP:", error);
        }
    };

    const handleBulkAddToOpenJob = () => {
        // Use the accumulated full candidate objects
        const selectedCandidates = selectedCandidatesData;

        if (selectedCandidates.length === 0) return;

        navigate('/candidate_data/addCandidate', {
            state: {
                prefillCandidates: selectedCandidates.map(row => ({
                    candidate_name: row.candidate_name,
                    candidate_current_location: row.candidate_current_location,
                    mobile: row.mobile,
                    email: row.email,
                    currency: row.currency,
                    budget: row.budget,
                    budget_eom: row.budget_eom,
                    gender: row.gender || row.CandidateDetails?.gender || "",
                    notice_period: (row.notice_period !== undefined && row.notice_period !== null && row.notice_period !== "")
                      ? row.notice_period
                      : (row.timeline || row.CandidateDetails?.notice_period || ""),
                    coreSkillId: row.coreSkillId || row.profileCoreSkill?.id,
                    techSkills: row.techSkills || (row.profileCoreSkill ? [row.profileCoreSkill.name] : []),
                    profile_path: (row.pipeline_type === 'non_resume_based' || row.Job?.pipeline_type === 'non_resume_based' || row.job_pipeline_type === 'non_resume_based' || (!row.profile_path && !row.resume_url && !row.url))
                      ? ""
                      : (row.profile_path || extractFileNameFromUrl(row.resume_url || row.url) || "Existing Resume"),
                    s3Key_profile_path: (row.pipeline_type === 'non_resume_based' || row.Job?.pipeline_type === 'non_resume_based' || row.job_pipeline_type === 'non_resume_based' || (!row.profile_path && !row.resume_url && !row.url))
                      ? ""
                      : (row.s3Key_profile_path || row.profile_path || row.resume_url || row.url || "Existing Resume"),
                    important_note: row.important_note || "",
                    aadhar_doc: row.aadhar_doc || "",
                    s3Key_aadhar_doc: row.s3Key_aadhar_doc || "",
                    pan_doc: row.pan_doc || "",
                    s3Key_pan_doc: row.s3Key_pan_doc || ""
                })),
                selectedSupplierId: selectedCandidates[0].supplier_id || selectedCandidates[0].supplierInfo?.supplier_id
            }
        });
    };

    // Logic for display
    const isSemanticActive = activeRagQuery.trim().length >= 3;
    const baseProfiles = isSemanticActive ? (ragData?.candidates || []) : profiles;

    const displayProfiles = useMemo(() => {
        if (statusFilter === 'All') return baseProfiles;
        return baseProfiles.filter(p => {
            const status = (p.status || p.status_group || 'Available').toLowerCase();
            return status === statusFilter.toLowerCase();
        });
    }, [baseProfiles, statusFilter]);

    const getScoreBadgeStyle = (score) => {
        if (score == null) return { color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1' };
        if (score >= 70) return { color: '#15803d', bg: '#dcfce7', border: '#86efac' };
        if (score >= 40) return { color: '#b45309', bg: '#fef3c7', border: '#fcd34d' };
        return { color: '#b91c1c', bg: '#fee2e2', border: '#fca5a5' };
    };

    const ragErrorCode = ragError?.response?.data?.error_code || ragError?.response?.data?.errorCode;
    const ragErrorStatus = ragError?.response?.status;
    const ragErrorMessage = ragError?.response?.data?.message || ragError?.message;

    const isInvalidQueryError = ragErrorCode === 'INVALID_QUERY' || (ragErrorStatus === 400 && ragErrorCode === 'INVALID_QUERY');
    const isRateLimitError = ragErrorStatus === 429 || ragErrorCode === 'RATE_LIMIT_EXCEEDED';
    const isGeneralError = Boolean(ragError && !isInvalidQueryError && !isRateLimitError);

    const getStatusChipStyles = (status) => {
        const s = status?.toLowerCase();
        if (s === 'available') return { bgcolor: '#22c55e20', color: '#22c55e', border: '1px solid #22c55e' };
        if (s === 'deployed') return { bgcolor: '#94a3b820', color: '#64748b', border: '1px solid #cbd5e1' };
        if (s === 'notice period') return { bgcolor: '#f9731620', color: '#f97316', border: '1px solid #f97316' };
        return { bgcolor: 'transparent', color: 'text.secondary', border: '1px solid #e2e8f0' };
    };

    const handleAiSearch = () => {
        setPage(1);
        setActiveRagQuery(ragQuery);
    };

    const handleAiKeyDown = (e) => {
        if (e.key === 'Enter') handleAiSearch();
    };

    return (
        <Box sx={{ p: 4, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <h2 className={classes.pageTitle} style={{ margin: 0 }}>CV Search</h2>
                {/* Search config is an admin-only screen — others get the search
                    itself, not the knobs behind it. */}
                {isAdmin && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<TuneIcon />}
                        onClick={() => navigate('/cv_search_config')}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                    >
                        Search Configuration
                    </Button>
                )}
            </Box>

            {/* AI Search Bar */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 3,
                    border: '1px solid #e2e8f0',
                    transition: '0.2s',
                    '&:focus-within': { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)' }
                }}
            >
                <Box sx={{ px: 2, display: 'flex', alignItems: 'center', color: '#3b82f6' }}>
                    <PsychologyIcon />
                </Box>
                <InputBase
                    fullWidth
                    placeholder="Describe the ideal candidate (e.g., 'Senior Data Engineer with Spark and AWS experience in Pune')"
                    value={ragQuery}
                    onChange={(e) => setRagQuery(e.target.value)}
                    onKeyDown={handleAiKeyDown}
                    sx={{ fontSize: '1rem', color: '#1e293b' }}
                    inputProps={{ maxLength: 500 }}
                />
                <Box sx={{ px: 2, color: 'text.disabled', fontSize: '0.75rem' }}>
                    {ragQuery.length}/500
                </Box>
                <Button
                    variant="contained"
                    onClick={handleAiSearch}
                    disabled={ragLoading || ragQuery.trim().length === 0}
                    sx={{
                        px: 4,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: '#3b82f6',
                        '&:hover': { bgcolor: '#2563eb' }
                    }}
                >
                    {ragLoading ? <CircularProgress size={24} color="inherit" /> : 'AI Search'}
                </Button>
            </Paper>

            {/* Error & Degraded State Alerts */}
            {isSemanticActive && isInvalidQueryError && (
                <Alert 
                    severity="info" 
                    icon={<PsychologyIcon fontSize="inherit" />}
                    sx={{ 
                        mb: 3, 
                        borderRadius: 3, 
                        border: '1px solid #bfdbfe',
                        bgcolor: '#eff6ff',
                        color: '#1e3a8a',
                        '& .MuiAlert-icon': { color: '#2563eb' }
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Refine Your Candidate Search
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#1e40af' }}>
                        {ragErrorMessage || "Please refine your search query to find candidates using skills, location, or experience (e.g. 'React developer with 3 years of experience in Pune')."}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#2563eb' }}>
                            Example queries:
                        </Typography>
                        {[
                            "React developer with 3+ years experience in Pune",
                            "Senior Python backend engineer with AWS and Docker",
                            "Full stack developer with Node.js and MongoDB",
                            "Data engineer with Spark and PySpark"
                        ].map((sampleQuery, idx) => (
                            <Chip
                                key={idx}
                                label={sampleQuery}
                                size="small"
                                onClick={() => {
                                    setRagQuery(sampleQuery);
                                    setActiveRagQuery(sampleQuery);
                                    setPage(1);
                                }}
                                sx={{
                                    cursor: 'pointer',
                                    bgcolor: 'white',
                                    border: '1px solid #93c5fd',
                                    color: '#1d4ed8',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    '&:hover': { bgcolor: '#dbeafe' }
                                }}
                            />
                        ))}
                    </Box>
                </Alert>
            )}

            {isSemanticActive && isRateLimitError && (
                <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                    <Typography variant="body2">
                        <strong>Rate Limit:</strong> {ragErrorMessage || 'Too many search requests. Please wait a moment before searching again.'}
                    </Typography>
                </Alert>
            )}

            {isSemanticActive && isGeneralError && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    <Typography variant="body2">
                        <strong>Search Error:</strong> {ragErrorMessage || 'Unable to search candidates. Please try again.'}
                    </Typography>
                </Alert>
            )}

            {isSemanticActive && ragData?.degraded === true && (
                <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                    <Typography variant="body2">
                        <strong>AI Search Temporarily Unavailable:</strong> {ragData?.message || 'The semantic search service is temporarily degraded. Showing standard results instead.'}
                    </Typography>
                </Alert>
            )}

            {/* Parsed Filters Display */}
            {isSemanticActive && ragData && !ragData.degraded && (
                <Box sx={{ mb: 3 }}>
                    <ParsedFiltersDisplay
                        parsedFilters={ragData.parsed_filters}
                        latencyMs={ragData.latency_ms}
                    />
                </Box>
            )}

            {/* Filter Row — Supplier | Skills | Bench Status | Date | Apply | Clear */}
            <Stack direction="row" flexWrap="wrap" sx={{ mb: 4, alignItems: 'center', gap: 1.5 }}>
                {/* Supplier */}
                {!isSupplier && (
                    <FormControl sx={{ minWidth: 180 }} size="small">
                        <InputLabel>Supplier</InputLabel>
                        <Select
                            value={selectedSupplier?.supplier_id || ''}
                            onChange={handleSupplierChange}
                            label="Supplier"
                            sx={{ borderRadius: 2, bgcolor: 'white' }}
                        >
                            <MenuItem value=""><em>All Suppliers</em></MenuItem>
                            {supplierData.map(s => (
                                <MenuItem key={s.supplier_id} value={s.supplier_id}>{s.supplier_org_name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Core Skills */}
                <FormControl sx={{ minWidth: 200 }} size="small">
                    <Select
                        multiple
                        displayEmpty
                        value={selectedCoreSkills.map(s => s.id)}
                        onChange={handleCoreSkillChange}
                        renderValue={(selected) => {
                            if (selected.length === 0) return <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>All Skills</Typography>;
                            return <Typography sx={{ fontSize: '0.9rem' }}>{selectedCoreSkills.map(s => s.name).join(', ')}</Typography>;
                        }}
                        sx={{ borderRadius: 2, bgcolor: 'white' }}
                    >
                        {allCoreSkills.map((skill) => (
                            <MenuItem key={skill.id} value={skill.id}>
                                <Checkbox checked={selectedCoreSkills.some(s => s.id === skill.id)} />
                                <ListItemText primary={skill.name} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Bench Status */}
                <FormControl sx={{ minWidth: 160 }} size="small">
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{ borderRadius: 2, bgcolor: 'white' }}
                    >
                        <MenuItem value="All">Bench Status (All)</MenuItem>
                        <MenuItem value="Available">Available</MenuItem>
                        <MenuItem value="Deployed">Deployed</MenuItem>
                        <MenuItem value="Notice Period">Notice Period</MenuItem>
                    </Select>
                </FormControl>

                {/* Date */}
                <FormControl sx={{ minWidth: 150 }} size="small">
                    <InputLabel>Date</InputLabel>
                    <Select
                        value={selectedDateFilter}
                        onChange={(e) => setSelectedDateFilter(e.target.value)}
                        label="Date"
                        sx={{ borderRadius: 2, bgcolor: 'white' }}
                    >
                        <MenuItem value="all">All Time</MenuItem>
                        <MenuItem value="today">Today</MenuItem>
                        <MenuItem value="week">This Week</MenuItem>
                        <MenuItem value="month">This Month</MenuItem>
                    </Select>
                </FormControl>

                {/* Actions */}
                <Button
                    variant="contained"
                    onClick={handleSubmitFilters}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
                >
                    Apply
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    sx={{ borderRadius: 2, textTransform: 'none', color: '#64748b', borderColor: '#e2e8f0' }}
                >
                    Clear
                </Button>
            </Stack>

            {/* Results Section */}
            <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'white' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {isSemanticActive ? 'Top Matches' : 'All Candidates'}
                        {isSemanticActive && ragData && (
                            <Typography component="span" variant="body2" sx={{ ml: 2, color: '#64748b', fontWeight: 400 }}>
                                Found {ragData.total_results} results
                            </Typography>
                        )}
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            disabled={selectedIds.length === 0}
                            onClick={handleBulkDownload}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                color: '#3b82f6',
                                borderColor: '#bfdbfe',
                                '&:hover': { bgcolor: '#eff6ff', borderColor: '#3b82f6' }
                            }}
                        >
                            Export Results {selectedIds.length > 0 && `(${selectedIds.length})`}
                        </Button>
                        {(isSupplier || isStaff) && (
                            <Button
                                variant="contained"
                                disabled={selectedIds.length === 0}
                                onClick={handleBulkAddToOpenJob}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    bgcolor: '#3b82f6',
                                    '&:hover': { bgcolor: '#2563eb' }
                                }}
                            >
                                Add to Open Job {selectedIds.length > 0 && `(${selectedIds.length})`}
                            </Button>
                        )}
                    </Stack>
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <Checkbox
                                        checked={displayProfiles.length > 0 && displayProfiles.every(r => selectedIds.includes(r.profile_id))}
                                        indeterminate={displayProfiles.some(r => selectedIds.includes(r.profile_id)) && !displayProfiles.every(r => selectedIds.includes(r.profile_id))}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedIds(prev => [...new Set([...prev, ...displayProfiles.map(p => p.profile_id)])]);
                                                setSelectedCandidatesData(prev => {
                                                    const newCandidates = displayProfiles.filter(p => !prev.some(c => c.profile_id === p.profile_id));
                                                    return [...prev, ...newCandidates];
                                                });
                                            } else {
                                                const visibleIds = displayProfiles.map(p => p.profile_id);
                                                setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
                                                setSelectedCandidatesData(prev => prev.filter(c => !visibleIds.includes(c.profile_id)));
                                            }
                                        }}
                                    />
                                </TableCell>
                                {isSemanticActive && (
                                    <TableCell sx={{ color: '#64748b', fontWeight: 600, borderBottom: '1px solid #f1f5f9', minWidth: 110 }}>
                                        Match Score
                                    </TableCell>
                                )}
                                <TableCell sx={{ color: '#64748b', fontWeight: 600, borderBottom: '1px solid #f1f5f9' }}>Candidate Name</TableCell>
                                <TableCell sx={{ color: '#64748b', fontWeight: 600, borderBottom: '1px solid #f1f5f9' }}>Resume</TableCell>
                                {!isSupplier && <TableCell sx={{ color: '#64748b', fontWeight: 600, borderBottom: '1px solid #f1f5f9' }}>Supplier</TableCell>}
                                <TableCell sx={{ color: '#64748b', fontWeight: 600, borderBottom: '1px solid #f1f5f9' }}>Core skills</TableCell>
                                <TableCell sx={{ color: '#64748b', fontWeight: 600, borderBottom: '1px solid #f1f5f9' }}>Created Date</TableCell>
                                <TableCell sx={{ color: '#64748b', fontWeight: 600, borderBottom: '1px solid #f1f5f9' }}>Status</TableCell>
                                <TableCell sx={{ color: '#64748b', fontWeight: 600, borderBottom: '1px solid #f1f5f9' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayProfiles.map((candidate) => {
                                const styles = getStatusChipStyles(candidate.status || candidate.status_group || 'Available');
                                return (
                                    <TableRow key={candidate.profile_id} hover sx={{ '&:hover': { bgcolor: '#f1f5f940' } }}>
                                        <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <Checkbox
                                                checked={selectedIds.includes(candidate.profile_id)}
                                                onChange={() => handleToggleOne(candidate.profile_id)}
                                            />
                                        </TableCell>
                                        {/* Match Score (Semantic Mode) */}
                                        {isSemanticActive && (
                                            <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                {(() => {
                                                    const scoreVal = candidate.match_score != null
                                                        ? Math.round(candidate.match_score)
                                                        : (candidate.similarity_score != null ? Math.round(candidate.similarity_score * 100) : null);
                                                    const badgeStyle = getScoreBadgeStyle(scoreVal);
                                                    return (
                                                        <Tooltip
                                                            title={
                                                                <Box sx={{ p: 0.5 }}>
                                                                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                                                                        Resume Match Breakdown
                                                                    </Typography>
                                                                    {candidate.matched_section && (
                                                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                                                            • Best match in: <strong>{candidate.matched_section}</strong>
                                                                        </Typography>
                                                                    )}
                                                                    {candidate.matched_skills?.length > 0 && (
                                                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                                                            • Matched skills: {candidate.matched_skills.join(', ')}
                                                                        </Typography>
                                                                    )}
                                                                    {candidate.missing_skills?.length > 0 && (
                                                                        <Typography variant="caption" sx={{ display: 'block', color: '#fca5a5' }}>
                                                                            • Missing skills: {candidate.missing_skills.join(', ')}
                                                                        </Typography>
                                                                    )}
                                                                    {candidate.rerank_score != null && (
                                                                        <Typography variant="caption" sx={{ display: 'block', color: '#cbd5e1' }}>
                                                                            • Rerank score: {candidate.rerank_score.toFixed(2)}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            }
                                                            arrow
                                                            placement="top"
                                                        >
                                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Chip
                                                                    label={scoreVal != null ? `${scoreVal}%` : '—'}
                                                                    size="small"
                                                                    sx={{
                                                                        fontWeight: 700,
                                                                        fontSize: '0.78rem',
                                                                        minWidth: 54,
                                                                        bgcolor: badgeStyle.bg,
                                                                        color: badgeStyle.color,
                                                                        border: `1px solid ${badgeStyle.border}`,
                                                                        cursor: 'help'
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Tooltip>
                                                    );
                                                })()}
                                            </TableCell>
                                        )}
                                        {/* Candidate Name */}
                                        <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                {candidate.candidate_name}
                                            </Typography>
                                        </TableCell>
                                        {/* Resume */}
                                        <TableCell sx={{ borderBottom: '1px solid #f1f5f9', maxWidth: 260 }}>
                                            <Typography variant="body2" sx={{ color: '#1e293b', wordBreak: 'break-word', fontSize: '0.82rem' }}>
                                                {(candidate.pipeline_type === 'non_resume_based' || candidate?.Job?.pipeline_type === 'non_resume_based' || candidate?.job_pipeline_type === 'non_resume_based')
                                                    ? 'NA'
                                                    : (extractFileNameFromUrl(candidate.resume_url || candidate.url) || '—')}
                                            </Typography>
                                        </TableCell>
                                        {/* Supplier */}
                                        {!isSupplier && (
                                            <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <Typography variant="body2" sx={{ color: '#1e293b', fontSize: '0.85rem' }}>
                                                    {candidate?.supplierInfo?.supplier_org_name || '—'}
                                                </Typography>
                                            </TableCell>
                                        )}
                                        {/* Core skills */}
                                        <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {isSemanticActive ? (
                                                    (() => {
                                                        const matchedSet = new Set((candidate.matched_skills || []).map(s => s.toLowerCase().trim()));
                                                        const allSkills = candidate.tech_skills || [];
                                                        return (
                                                            <>
                                                                {allSkills.slice(0, 4).map((skill, i) => {
                                                                    const isMatched = matchedSet.has(skill.toLowerCase().trim());
                                                                    return (
                                                                        <Chip
                                                                            key={i}
                                                                            label={skill}
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: isMatched ? '#dbeafe' : 'white',
                                                                                color: isMatched ? '#1d4ed8' : '#334155',
                                                                                border: isMatched ? '1px solid #93c5fd' : '1px solid #e2e8f0',
                                                                                fontWeight: isMatched ? 600 : 400,
                                                                                fontSize: '0.7rem'
                                                                            }}
                                                                        />
                                                                    );
                                                                })}
                                                                {allSkills.length > 4 && (
                                                                    <Typography variant="caption" sx={{ color: '#64748b', alignSelf: 'center', ml: 0.5 }}>
                                                                        +{allSkills.length - 4} more
                                                                    </Typography>
                                                                )}
                                                            </>
                                                        );
                                                    })()
                                                ) : (
                                                    candidate.profileCoreSkill && (
                                                        <Chip label={candidate.profileCoreSkill.name} size="small" sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', fontSize: '0.7rem' }} />
                                                    )
                                                )}
                                            </Box>
                                        </TableCell>
                                        {/* Created Date */}
                                        <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <Typography variant="body2" sx={{ color: '#1e293b', fontSize: '0.85rem' }}>
                                                {candidate.createdAt ? format(new Date(candidate.createdAt), 'dd-MM-yyyy') : '—'}
                                            </Typography>
                                        </TableCell>
                                        {/* Status */}
                                        <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <Chip
                                                label={(candidate.status || candidate.status_group || 'Available').replace(/_/g, ' ')}
                                                size="small"
                                                sx={{ ...styles, fontWeight: 500, fontSize: '0.7rem', textTransform: 'capitalize' }}
                                            />
                                        </TableCell>
                                        {/* Actions */}
                                        <TableCell align="left" sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, candidate.profile_id)}>
                                                <MoreVertIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}

                            {displayProfiles.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={isSemanticActive ? (isSupplier ? 8 : 9) : (isSupplier ? 7 : 8)} sx={{ py: 10, textAlign: 'center' }}>
                                        <Typography variant="body1" sx={{ color: '#64748b' }}>
                                            {ragLoading ? 'Searching for best matches...' : (isSemanticActive ? `No candidates found for "${activeRagQuery}"` : 'No resumes found for applied filters.')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', bgcolor: 'white' }}>
                    <Pagination
                        count={isSemanticActive ? Math.ceil((ragData?.total_results || 0) / 10) : Math.ceil(listCount / 10)}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        size="medium"
                        sx={{
                            '& .MuiPaginationItem-root': { borderRadius: 2 },
                            '& .Mui-selected': { bgcolor: '#3b82f6 !important' }
                        }}
                    />
                </Box>
            </Paper>

            {/* Row Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
                PaperProps={{ sx: { minWidth: 180, borderRadius: 2, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' } }}
            >
                <MenuItem
                    disabled={(() => {
                        const row = isSemanticActive ? ragData?.candidates?.find(c => c.profile_id === menuRowId) : profiles.find(p => p.profile_id === menuRowId);
                        return !row || row.pipeline_type === 'non_resume_based' || row.Job?.pipeline_type === 'non_resume_based' || row.job_pipeline_type === 'non_resume_based' || (!row.resume_url && !row.url);
                    })()}
                    onClick={() => {
                        const row = isSemanticActive ? ragData?.candidates?.find(c => c.profile_id === menuRowId) : profiles.find(p => p.profile_id === menuRowId);
                        const fileUrl = row?.resume_url || row?.url;
                         if (fileUrl) {
                             navigate('/admindashboard/viewFile', {
                                 state: {
                                     fileUrl,
                                     from: window.location.pathname + window.location.search
                                 }
                             });
                         }
                         handleMenuClose();
                    }}>
                    <Typography variant="body2">View Detailed Profile</Typography>
                </MenuItem>
                <MenuItem
                    disabled={(() => {
                        const row = isSemanticActive ? ragData?.candidates?.find(c => c.profile_id === menuRowId) : profiles.find(p => p.profile_id === menuRowId);
                        return !row || row.pipeline_type === 'non_resume_based' || row.Job?.pipeline_type === 'non_resume_based' || row.job_pipeline_type === 'non_resume_based' || (!row.resume_url && !row.url);
                    })()}
                    onClick={() => handleDownload(menuRowId)}>
                    <Typography variant="body2">Download Resume</Typography>
                </MenuItem>
                {(isSupplier || isStaff) && (
                    <MenuItem onClick={() => {
                        const row = isSemanticActive 
                            ? (ragData?.candidates || []).find(c => c.profile_id === menuRowId) 
                            : profiles.find(p => p.profile_id === menuRowId);
                        if (row) {
                            navigate('/candidate_data/addCandidate', {
                                state: {
                                    prefillCandidates: [{
                                        candidate_name: row.candidate_name,
                                        candidate_current_location: row.candidate_current_location,
                                        mobile: row.mobile,
                                        email: row.email,
                                        currency: row.currency,
                                        budget: row.budget,
                                        budget_eom: row.budget_eom,
                                        gender: row.gender || row.CandidateDetails?.gender || "",
                                        notice_period: (row.notice_period !== undefined && row.notice_period !== null && row.notice_period !== "")
                                          ? row.notice_period
                                          : (row.timeline || row.CandidateDetails?.notice_period || ""),
                                        coreSkillId: row.coreSkillId || row.profileCoreSkill?.id,
                                        techSkills: row.techSkills || (row.profileCoreSkill ? [row.profileCoreSkill.name] : []),
                                        profile_path: (row.pipeline_type === 'non_resume_based' || row.Job?.pipeline_type === 'non_resume_based' || row.job_pipeline_type === 'non_resume_based' || (!row.profile_path && !row.resume_url && !row.url))
                                          ? ""
                                          : (row.profile_path || extractFileNameFromUrl(row.resume_url || row.url) || "Existing Resume"),
                                        s3Key_profile_path: (row.pipeline_type === 'non_resume_based' || row.Job?.pipeline_type === 'non_resume_based' || row.job_pipeline_type === 'non_resume_based' || (!row.profile_path && !row.resume_url && !row.url))
                                          ? ""
                                          : (row.s3Key_profile_path || row.profile_path || row.resume_url || row.url || "Existing Resume"),
                                        important_note: row.important_note || "",
                                        aadhar_doc: row.aadhar_doc || "",
                                        s3Key_aadhar_doc: row.s3Key_aadhar_doc || "",
                                        pan_doc: row.pan_doc || "",
                                        s3Key_pan_doc: row.s3Key_pan_doc || ""
                                    }],
                                    selectedSupplierId: row.supplier_id || row.supplierInfo?.supplier_id
                                }
                            });
                        }
                        handleMenuClose();
                    }}>
                        <Typography variant="body2" sx={{ color: '#2563eb', fontWeight: 500 }}>Add to Open Job</Typography>
                    </MenuItem>
                )}
            </Menu>
        </Box>
    );
};

export default AiCvSearchView;
